import {
	Application,
	Extension,
	ProctoringAssessmentConfigurationV2,
	ProctoringOption,
	ProctoringPlatforms,
} from "@verity/proctoring-option/types";
import { ProctoringOptionCategory } from "../proctoring-options/option-text";
import { VerityApi } from "../../api/fetch";
import { useMobileLayout } from "../../hooks";
import { useState } from "react";
import EnforceProfileIcon from "../images/enforceProfile.svg?react";
import { CoreSpinner } from "../../core";
import ProctoringOptionsList from "../proctoring-options/proctoringOptionsList";
import ProctoringOptions from "../proctoring-options/proctoringOptionsGrid";

type ProctorSettingsProps = {
	option: Partial<ProctoringOption>;
	updateOption: (option: Partial<ProctoringOption>) => void;
	disabled?: boolean;
	shouldShowOption: (o: keyof ProctoringOption) => boolean;
	isOptionAllowed: <O extends keyof ProctoringOption>(option: O, value?: ProctoringOption[O]) => boolean;
	getPartiallySupportedPlatforms: <O extends keyof ProctoringOption>(
		option: O,
		value?: ProctoringOption[O],
	) => Set<keyof ProctoringPlatforms>;
	loading?: boolean;
	applications: Application[];
	fetchChromeExtensionData: (id: string) => Promise<Extension | null>;
	extensions: Extension[];
	allowedCategories: ProctoringOptionCategory[];
	currentCategory: number;
	setCurrentCategory: React.Dispatch<React.SetStateAction<number>>;
	proctoringAssessmentConfiguration: Omit<Partial<ProctoringAssessmentConfigurationV2>, "proctoringOptionProfile">;
	setOptionBodyRef?: (el: HTMLDivElement | null) => void;
	instituteUuid?: string;
	getInstituteSettings?: VerityApi["getInstituteSettings"];
	courseId?: number;
};

export default function ProctorSettings({
	option,
	updateOption,
	disabled,
	shouldShowOption,
	isOptionAllowed,
	loading,
	applications,
	fetchChromeExtensionData,
	extensions,
	allowedCategories,
	currentCategory,
	setCurrentCategory,
	proctoringAssessmentConfiguration,
	setOptionBodyRef,
	instituteUuid,
	getInstituteSettings,
	getPartiallySupportedPlatforms,
	courseId,
}: ProctorSettingsProps) {
	const isMobileLayout = useMobileLayout();
	const [hasPassword] = useState(
		Boolean(
			proctoringAssessmentConfiguration.thirdPartyAssessmentOption?.password &&
				proctoringAssessmentConfiguration.thirdPartyAssessmentOption?.password !== "",
		),
	);
	return (
		<div>
			{disabled ? (
				<div style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
					<div style={{ textAlign: "center", width: "80%", marginBottom: "20px" }}>
						Your institution has chosen your configuration settings. You can include live proctors or exempt students
						for this assessment.
					</div>
					<EnforceProfileIcon />
				</div>
			) : (
				<div>
					{loading ? (
						<div style={{ display: "flex", justifyContent: "center" }}>
							<CoreSpinner />
						</div>
					) : isMobileLayout ? (
						<ProctoringOptionsList
							option={option}
							updateOption={updateOption}
							disabled={disabled}
							shouldShowOption={shouldShowOption}
							isOptionAllowed={isOptionAllowed}
							getPartiallySupportedPlatforms={getPartiallySupportedPlatforms}
							applications={applications}
							fetchChromeExtensionData={fetchChromeExtensionData}
							extensions={extensions}
							allowedCategories={allowedCategories}
							hasPassword={hasPassword}
							setBodyRef={setOptionBodyRef}
							instituteUuid={instituteUuid}
							getInstituteSettings={getInstituteSettings}
							courseId={courseId}
						/>
					) : (
						<ProctoringOptions
							option={option}
							updateOption={updateOption}
							disabled={disabled}
							shouldShowOption={shouldShowOption}
							isOptionAllowed={isOptionAllowed}
							getPartiallySupportedPlatforms={getPartiallySupportedPlatforms}
							applications={applications}
							extensions={extensions}
							fetchChromeExtensionData={fetchChromeExtensionData}
							allowedCategories={allowedCategories}
							currentCategory={currentCategory}
							setCurrentCategory={setCurrentCategory}
							hasPassword={hasPassword}
							setBodyRef={setOptionBodyRef}
							instituteUuid={instituteUuid}
							getInstituteSettings={getInstituteSettings}
							courseId={courseId}
						/>
					)}
				</div>
			)}
		</div>
	);
}
